import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import { supabase } from '../lib/supabase'

const events = [
  {
    id: 'yamka-games',
    name: 'Allign Sessions',
    tagline: 'No egos, just football.',
    description: 'Regular pickup sessions open to all. 18 slots per session, first come first served. Show up, play hard, leave better.',
    type: 'participation',
    image: '/images/Hero-4.jpg',
    status: 'active',
    cta: 'Book a Slot',
  },
  {
    id: 'pathway-to-pro',
    name: 'Pathway to Pro',
    tagline: 'Elevate your game.',
    description: 'Structured development sessions for players looking to take their game to the next level. Professional coaching, tactical analysis, and performance feedback.',
    type: 'development',
    image: '/images/000022270027.jpg',
    status: 'coming-soon',
    cta: 'Get Notified',
  },
  {
    id: 'watch-club',
    name: 'Watch Club',
    tagline: 'Experience the game together.',
    description: 'Communal viewing experiences for the matches that matter. Big screens, good company, and the shared energy of watching the game together.',
    type: 'community',
    image: '/images/watch-club.jpg',
    status: 'active',
    cta: 'Vote for Fixtures',
  },
]

const ptpImages = [
  '/images/ptp-event-1.jpg',
  '/images/ptp-event-2.jpg',
  '/images/ptp-event-3.jpg',
]

export default function Events({ onLoginClick }) {
  const [nextSession, setNextSession] = useState(null)
  const [slotsBooked, setSlotsBooked] = useState(0)
  const [ptpSlide, setPtpSlide] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setPtpSlide(prev => (prev + 1) % ptpImages.length)
    }, 4000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    fetchNextSession()
  }, [])

  const fetchNextSession = async () => {
    try {
      const today = new Date().toISOString().split('T')[0]
      const { data: sessions } = await supabase
        .from('sessions')
        .select('*, events!inner(name, type)')
        .eq('events.type', 'yamka')
        .gte('date', today)
        .order('date', { ascending: true })
        .limit(1)

      if (sessions?.length > 0) {
        setNextSession(sessions[0])

        // Get bookings count
        const { count } = await supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .eq('session_id', sessions[0].id)

        setSlotsBooked(count || 0)
      }
    } catch (error) {
      console.error('Error fetching next session:', error)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
  }

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour % 12 || 12
    return `${hour12}:${minutes} ${ampm}`
  }

  const featuredEvent = events[0] // Allign Sessions

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title="Events"
        description="Join DOTTEDLINE football events: Allign Sessions pickup sessions, Watch Club viewing experiences, and Pathway to Pro development sessions."
        url="/events"
      />
      <Navbar onLoginClick={onLoginClick} variant="dark" />

      {/* Hero / Featured Event */}
      <section className="relative h-[70vh] min-h-[500px] flex items-end">
        <div className="absolute inset-0">
          <img
            src={featuredEvent.image}
            alt={featuredEvent.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>

        <div className="relative z-10 w-full px-6 pb-12 md:pb-16">
          <div className="max-w-5xl mx-auto">
            <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium uppercase tracking-wider mb-4">
              Featured Event
            </span>
            <h1 className="text-4xl md:text-6xl text-white tracking-tight mb-3">
              {featuredEvent.name}
            </h1>
            <p className="text-xl md:text-2xl text-white/80 mb-6 max-w-2xl">
              {featuredEvent.tagline}
            </p>

            {/* Next Session Info */}
            {nextSession && (
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                  <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-white text-sm font-medium">
                    {formatDate(nextSession.date)}
                  </span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                  <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-white text-sm font-medium">
                    {formatTime(nextSession.time)}
                  </span>
                </div>
                {slotsBooked >= 5 && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                    <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-white text-sm font-medium">
                      {slotsBooked}/{nextSession.max_slots || 18} booked
                    </span>
                  </div>
                )}
              </div>
            )}

            <Link
              to="/events/yamka-games"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-gray-100 transition-colors"
            >
              {featuredEvent.cta}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <main className="flex-1 py-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <header className="mb-12">
            <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
              All Events
            </h2>
            <p className="text-3xl font-semibold tracking-tight">
              Ways to engage with the game.
            </p>
          </header>

          {/* Event Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="group block bg-white border border-gray-200 overflow-hidden hover:border-black transition-colors"
              >
                {event.id === 'pathway-to-pro' ? (
                  /* Slideshow for Pathway to Pro */
                  <div className="aspect-[4/3] relative overflow-hidden">
                    {ptpImages.map((src, i) => (
                      <img
                        key={src}
                        src={src}
                        alt={`Pathway to Pro event ${i + 1}`}
                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                        style={{ opacity: i === ptpSlide ? 1 : 0 }}
                      />
                    ))}
                    {/* Memories note */}
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3">
                      <p className="text-white/90 text-xs font-medium uppercase tracking-widest">
                        Memories from our last event
                      </p>
                    </div>
                    {/* Slide dots */}
                    <div className="absolute top-3 right-3 flex gap-1">
                      {ptpImages.map((_, i) => (
                        <span
                          key={i}
                          className="block rounded-full transition-all duration-300"
                          style={{
                            width: i === ptpSlide ? 16 : 6,
                            height: 6,
                            background: i === ptpSlide ? '#fff' : 'rgba(255,255,255,0.45)',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      {event.type}
                    </span>
                    {event.status === 'coming-soon' && (
                      <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        Coming Soon
                      </span>
                    )}
                    {event.status === 'active' && (
                      <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        Active
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-gray-600 transition-colors">
                    {event.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                    {event.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
